import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, ButtonProps, Menu, MenuItem, MenuProps, styled } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TimelapseIcon from '@mui/icons-material/Timelapse';
import AlarmOnIcon from '@mui/icons-material/AlarmOn';
import ArchiveIcon from '@mui/icons-material/Archive';
import { PopoverOrigin } from '@material-ui/core';

import { selectUser } from 'entities/user/selectors';
import {
  GivingAccountWithdrawalStatus,
  ProviderType,
  useMarkGivingAccountWithdrawalAsReadyForProcessingMutation,
  useMarkGivingAccountWithdrawalAsProcessingMutation,
  useMarkGivingAccountWithdrawalAsPendingMutation,
} from 'generated/graphql';
import { CurrencyCode } from 'utils/currencyFormat';
import { Privileges } from 'utils/privileges';

import { MarkGivingAccountWithdrawalAsProcessedModal } from '../MarkGivingAccountWithdrawalAsProcessedModal';
import { Transaction } from '../types';

const GivingAccountWithdrawalStatusMap = {
  [GivingAccountWithdrawalStatus.Cancelled]: 'Cancelled',
  [GivingAccountWithdrawalStatus.Pending]: 'Pending',
  [GivingAccountWithdrawalStatus.Processed]: 'Processed',
  [GivingAccountWithdrawalStatus.Processing]: 'Processing',
  [GivingAccountWithdrawalStatus.ReadyForProcessing]: 'Ready For Processing',
  [GivingAccountWithdrawalStatus.Archived]: 'Archived',
};

const GivingAccountWithdrawalStatusToIcon = {
  [GivingAccountWithdrawalStatus.Cancelled]: CancelIcon,
  [GivingAccountWithdrawalStatus.Pending]: AccessTimeIcon,
  [GivingAccountWithdrawalStatus.Processed]: CheckCircleIcon,
  [GivingAccountWithdrawalStatus.Processing]: TimelapseIcon,
  [GivingAccountWithdrawalStatus.ReadyForProcessing]: AlarmOnIcon,
  [GivingAccountWithdrawalStatus.Archived]: ArchiveIcon,
};

const StyledButton = styled(Button)<ButtonProps>(() => ({
  minWidth: '67%',
}));

const menuStyleAttributes: Partial<MenuProps> = {
  sx: {
    my: 1,
  },
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'right',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'right',
  },
  MenuListProps: {
    disablePadding: true,
  },
};

const SetPendingButton = (props: {
  status: GivingAccountWithdrawalStatus;
  onClickHandler: (event: React.MouseEvent<HTMLButtonElement>) => void;
}) => {
  const { status, onClickHandler } = props;
  const buttonMessage = GivingAccountWithdrawalStatusMap[GivingAccountWithdrawalStatus.Pending];
  const CurrentStatusIcon =
    GivingAccountWithdrawalStatusToIcon[GivingAccountWithdrawalStatus.Pending];

  if (status !== GivingAccountWithdrawalStatus.ReadyForProcessing) {
    return null;
  }

  return (
    <>
      <MenuItem>
        <StyledButton size="small" onClick={onClickHandler} endIcon={<CurrentStatusIcon />}>
          {buttonMessage}
        </StyledButton>
      </MenuItem>
    </>
  );
};

const SetReadyForProcessingButton = (props: {
  status: GivingAccountWithdrawalStatus;
  onClickHandler: (event: React.MouseEvent<HTMLButtonElement>) => void;
}) => {
  const { status, onClickHandler } = props;
  const buttonMessage =
    GivingAccountWithdrawalStatusMap[GivingAccountWithdrawalStatus.ReadyForProcessing];
  const CurrentStatusIcon =
    GivingAccountWithdrawalStatusToIcon[GivingAccountWithdrawalStatus.ReadyForProcessing];

  if (status !== GivingAccountWithdrawalStatus.Pending) {
    return null;
  }

  return (
    <>
      <MenuItem>
        <StyledButton size="small" onClick={onClickHandler} endIcon={<CurrentStatusIcon />}>
          {buttonMessage}
        </StyledButton>
      </MenuItem>
    </>
  );
};

const SetProcessingButton = (props: {
  status: GivingAccountWithdrawalStatus;
  onClickHandler: (event: React.MouseEvent<HTMLButtonElement>) => void;
}) => {
  const { status, onClickHandler } = props;
  const buttonMessage = GivingAccountWithdrawalStatusMap[GivingAccountWithdrawalStatus.Processing];
  const CurrentStatusIcon =
    GivingAccountWithdrawalStatusToIcon[GivingAccountWithdrawalStatus.Processing];

  if (status !== GivingAccountWithdrawalStatus.ReadyForProcessing) {
    return null;
  }

  return (
    <MenuItem>
      <StyledButton size="small" onClick={onClickHandler} endIcon={<CurrentStatusIcon />}>
        {buttonMessage}
      </StyledButton>
    </MenuItem>
  );
};

const SetProcessedButton = (props: {
  status: GivingAccountWithdrawalStatus;
  onClickHandler: (event: React.MouseEvent<HTMLButtonElement>) => void;
}) => {
  const { status, onClickHandler } = props;

  const buttonMessage = GivingAccountWithdrawalStatusMap[GivingAccountWithdrawalStatus.Processed];
  const CurrentStatusIcon =
    GivingAccountWithdrawalStatusToIcon[GivingAccountWithdrawalStatus.Processed];

  if (status !== GivingAccountWithdrawalStatus.Processing) {
    return null;
  }

  return (
    <MenuItem>
      <StyledButton size="small" onClick={onClickHandler} endIcon={<CurrentStatusIcon />}>
        {buttonMessage}
      </StyledButton>
    </MenuItem>
  );
};

const open = (element: HTMLElement | null) => Boolean(element);

export const BatchFileStatusButton = ({
  givingAccountWithdrawal,
  accountCurrency,
  accountProvider,
}: {
  givingAccountWithdrawal: Extract<Transaction, { __typename: 'GivingAccountWithdrawal' }>;
  accountCurrency: CurrencyCode;
  accountProvider: ProviderType;
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const [
    markGivingAccountWithdrawalTransactionAsProcessedModalOpen,
    setMarkGivingAccountWithdrawalTransactionAsProcessedModalOpen,
  ] = useState(false);

  const user = useSelector(selectUser);

  const { status: givingAccountWithdrawalStatus } = givingAccountWithdrawal;

  const [markGivingAccountWithdrawalAsPending, { loading: loadingPendingStatusChange }] =
    useMarkGivingAccountWithdrawalAsPendingMutation({
      refetchQueries: ['givingAccountSummary', 'givingAccountTransactions'],
      variables: {
        id: givingAccountWithdrawal.id,
      },
    });

  const [
    markGivingAccountWithdrawalAsReadyForProcessing,
    { loading: loadingReadyForProcessingStatusChange },
  ] = useMarkGivingAccountWithdrawalAsReadyForProcessingMutation({
    refetchQueries: ['givingAccountSummary', 'givingAccountTransactions'],
    variables: {
      id: givingAccountWithdrawal.id,
      data: {},
    },
  });

  const [markGivingAccountWithdrawalAsProcessing, { loading: loadingProcessingStatusChange }] =
    useMarkGivingAccountWithdrawalAsProcessingMutation({
      refetchQueries: ['givingAccountSummary', 'givingAccountTransactions'],
      variables: {
        id: givingAccountWithdrawal.id,
        data: {},
      },
    });

  if (!user) {
    return null;
  }

  const handleCloseMarkGivingAccountWithdrawalAsProcessedModal = () => {
    setMarkGivingAccountWithdrawalTransactionAsProcessedModalOpen(false);
  };

  const handleCurrentStatusButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    event.stopPropagation();
  };

  const handleClose = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(null);
    event.stopPropagation();
  };

  const handleMarkGivingAccountWithdrawalAsPending = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    handleClose(event);
    markGivingAccountWithdrawalAsPending({
      variables: {
        id: givingAccountWithdrawal.id,
      },
    });
  };

  const handleMarkGivingAccountWithdrawalAsReadyForProcessing = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    handleClose(event);
    markGivingAccountWithdrawalAsReadyForProcessing({
      variables: {
        id: givingAccountWithdrawal.id,
        data: {},
      },
    });
  };

  const handleMarkGivingAccountWithdrawalAsProcessed = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    handleClose(event);
    setMarkGivingAccountWithdrawalTransactionAsProcessedModalOpen(true);
  };

  const handleMarkGivingAccountWithdrawalAsProcessing = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    handleClose(event);
    markGivingAccountWithdrawalAsProcessing({
      variables: {
        id: givingAccountWithdrawal.id,
      },
    });
  };

  const CurrentStatusIcon = GivingAccountWithdrawalStatusToIcon[givingAccountWithdrawal.status];
  const isLoading: boolean =
    loadingPendingStatusChange ||
    loadingReadyForProcessingStatusChange ||
    loadingProcessingStatusChange;

  const canChangeStatus = !(
    isLoading ||
    givingAccountWithdrawal.status === GivingAccountWithdrawalStatus.Archived ||
    !user.hasPrivilege(Privileges.BUDGET_LEDGER_MANAGE)
  );

  return (
    <>
      {markGivingAccountWithdrawalTransactionAsProcessedModalOpen && (
        <MarkGivingAccountWithdrawalAsProcessedModal
          open={markGivingAccountWithdrawalTransactionAsProcessedModalOpen}
          onClickClose={handleCloseMarkGivingAccountWithdrawalAsProcessedModal}
          givingAccountWithdrawal={givingAccountWithdrawal}
          accountCurrency={accountCurrency}
          accountProvider={accountProvider}
        />
      )}
      <StyledButton
        size="small"
        onClick={handleCurrentStatusButtonClick}
        endIcon={<CurrentStatusIcon />}
        disabled={!canChangeStatus}
        style={{ maxWidth: 'auto' }}
      >
        {GivingAccountWithdrawalStatusMap[givingAccountWithdrawal.status]}
      </StyledButton>
      <Menu
        anchorEl={anchorEl}
        open={open(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ ...menuStyleAttributes.anchorOrigin } as PopoverOrigin}
        sx={menuStyleAttributes.sx}
        transformOrigin={{ ...menuStyleAttributes.transformOrigin } as PopoverOrigin}
        MenuListProps={{ ...menuStyleAttributes.MenuListProps }}
      >
        <SetPendingButton
          status={givingAccountWithdrawalStatus}
          onClickHandler={handleMarkGivingAccountWithdrawalAsPending}
        ></SetPendingButton>
        <SetReadyForProcessingButton
          status={givingAccountWithdrawalStatus}
          onClickHandler={handleMarkGivingAccountWithdrawalAsReadyForProcessing}
        ></SetReadyForProcessingButton>
        <SetProcessingButton
          status={givingAccountWithdrawalStatus}
          onClickHandler={handleMarkGivingAccountWithdrawalAsProcessing}
        ></SetProcessingButton>
        <SetProcessedButton
          status={givingAccountWithdrawalStatus}
          onClickHandler={handleMarkGivingAccountWithdrawalAsProcessed}
        ></SetProcessedButton>
      </Menu>
    </>
  );
};
